import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import '../../styles/OwnerReportsTab.css';
import { FiFileText, FiCheckCircle, FiClock, FiCalendar, FiRepeat } from 'react-icons/fi';

const OwnerReportsTab = ({ dogOwnerReports = { assignments: [], submissions: [] }, dogId }) => {
    const [dogReports, setDogReports] = useState({ assignments: [], submissions: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeSubmissionId, setActiveSubmissionId] = useState(null);
    const [fillModalOpen, setFillModalOpen] = useState(false);
    const [viewSubmissionOpen, setViewSubmissionOpen] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [formAnswers, setFormAnswers] = useState({});

    // Fetch dog reports
    const fetchDogReports = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');
            const response = await axios.get(`/api/v1/reports/dog/${dogId}/reports`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDogReports(response.data);
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError('Nie udało się pobrać raportów');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dogId) {
            fetchDogReports();
        }
    }, [dogId]);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Nie określono';
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Get recurrence label
    const getRecurrenceLabel = (recurrence) => {
        const recurrenceMap = {
            'ONCE': 'Jednorazowo',
            'DAILY': 'Codziennie',
            'EVERY_2_DAYS': 'Co 2 dni',
            'EVERY_3_DAYS': 'Co 3 dni',
            'EVERY_4_DAYS': 'Co 4 dni',
            'EVERY_5_DAYS': 'Co 5 dni',
            'EVERY_6_DAYS': 'Co 6 dni',
            'WEEKLY': 'Tygodniowo'
        };
        return recurrenceMap[recurrence] || recurrence;
    };

    // Function to generate dates based on recurrence pattern
    function generateDatesBasedOnRecurrence(startDate, endDate, recurrence) {
        const dates = [];
        let currentDate = new Date(startDate);

        // Loop until we reach or exceed the end date
        while (currentDate <= endDate) {
            dates.push(new Date(currentDate)); // Add a copy of the current date

            // Move to the next date based on recurrence pattern
            switch (recurrence) {
                case 'ONCE':
                    // For one-time reports, just add the start date
                    currentDate = new Date(endDate.getTime() + 86400000); // Jump past end date to exit loop
                    break;

                case 'DAILY':
                    currentDate.setDate(currentDate.getDate() + 1);
                    break;

                case 'EVERY_2_DAYS':
                    currentDate.setDate(currentDate.getDate() + 2);
                    break;

                case 'EVERY_3_DAYS':
                    currentDate.setDate(currentDate.getDate() + 3);
                    break;

                case 'EVERY_4_DAYS':
                    currentDate.setDate(currentDate.getDate() + 4);
                    break;

                case 'EVERY_5_DAYS':
                    currentDate.setDate(currentDate.getDate() + 5);
                    break;

                case 'EVERY_6_DAYS':
                    currentDate.setDate(currentDate.getDate() + 6);
                    break;

                case 'WEEKLY':
                    currentDate.setDate(currentDate.getDate() + 7);
                    break;

                default:
                    // Default to daily if recurrence pattern is unknown
                    currentDate.setDate(currentDate.getDate() + 1);
            }
        }

        return dates;
    }

    // Group submissions by assignment with complete date coverage
    const submissionsByAssignment = dogReports.assignments.map(assignment => {
        // Get all submissions for this assignment
        let assignmentSubmissions = dogReports.submissions
            .filter(submission => submission.assignmentId === assignment.id);

        // Create a Map keyed by date string to handle duplicates
        const submissionsByDate = new Map();

        // First, add all existing submissions to our map
        assignmentSubmissions.forEach(submission => {
            const dueDateStr = submission.dueDate ? new Date(submission.dueDate).toISOString().split('T')[0] : null;

            // If we don't have this date yet, or if this submission is completed and the existing one isn't,
            // use this submission
            if (!submissionsByDate.has(dueDateStr) ||
                (submission.completed && !submissionsByDate.get(dueDateStr).completed)) {
                submissionsByDate.set(dueDateStr, submission);
            }
        });

        // Now generate all expected dates based on recurrence pattern
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of today

        const startDate = new Date(assignment.startDate);
        startDate.setHours(0, 0, 0, 0); // Set to beginning of start date

        // Determine the end limit for generating submissions
        let endLimit = today;
        if (assignment.endDate) {
            const assignmentEndDate = new Date(assignment.endDate);
            assignmentEndDate.setHours(0, 0, 0, 0);
            // Use the earlier of today or assignment end date
            endLimit = assignmentEndDate < today ? assignmentEndDate : today;
        }

        // Only proceed if the assignment has started
        if (startDate <= endLimit) {
            // Generate all dates from start date to end limit based on recurrence
            const allExpectedDates = generateDatesBasedOnRecurrence(
                startDate,
                endLimit,
                assignment.recurrence
            );

            // Add any missing dates as pending reports
            allExpectedDates.forEach(date => {
                const dateStr = date.toISOString().split('T')[0];

                // If we don't already have a submission for this date, create one
                if (!submissionsByDate.has(dateStr)) {
                    // Create a placeholder submission
                    const pendingSubmission = {
                        id: `pending-${assignment.id}-${date.getTime()}`, // Generate a temporary ID
                        assignmentId: assignment.id,
                        submittedAt: null,
                        dueDate: date.toISOString(),
                        completed: false,
                        template: {
                            id: assignment.reportTemplateId,
                            title: assignment.reportTemplateTitle,
                            questions: [] // This will be populated when the user opens the form
                        },
                        answers: []
                    };

                    submissionsByDate.set(dateStr, pendingSubmission);
                }
            });
        }

        // Convert Map back to array
        const completeSubmissions = Array.from(submissionsByDate.values());

        // Sort submissions by due date
        completeSubmissions.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

        return {
            assignment,
            submissions: completeSubmissions
        };
    });

    // Handle submission item click with proper event handling
    const handleSubmissionClick = (e, submissionId) => {
        // Toggle the active submission
        setActiveSubmissionId(activeSubmissionId === submissionId ? null : submissionId);
    };

    // Open fill report modal
    const handleFillReport = (submission) => {
        setSelectedSubmission(submission);

        // Initialize form answers based on template questions
        const initialAnswers = {};
        if (submission.template && submission.template.questions) {
            submission.template.questions.forEach(question => {
                initialAnswers[question.id] = '';
            });
            setFormAnswers(initialAnswers);
        }

        setFillModalOpen(true);
    };

    // Open view submission modal
    const handleViewSubmission = (submission) => {
        setSelectedSubmission(submission);
        setViewSubmissionOpen(true);
    };

    // Handle form input changes
    const handleAnswerChange = (questionId, value) => {
        setFormAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    // Submit report
    const handleSubmitReport = async () => {
        try {
            setLoading(true);

            // Validate - make sure all questions have answers
            const unansweredQuestions = selectedSubmission.template.questions.filter(
                question => !formAnswers[question.id] || formAnswers[question.id].trim() === ''
            );

            if (unansweredQuestions.length > 0) {
                const questionsText = unansweredQuestions.map((q, i) => `${i+1}. ${q.questionText}`).join('\n');
                alert(`Proszę odpowiedzieć na wszystkie pytania:\n${questionsText}`);
                setLoading(false);
                return;
            }

            // Create an array of answer objects
            const answers = [];

            // Add an answer entry for each question - ensure format matches what backend expects
            selectedSubmission.template.questions.forEach(question => {
                answers.push({
                    questionId: question.id,
                    answerText: formAnswers[question.id] || ''
                });
            });

            // Create the submission request payload
            const submissionData = {
                submissionId: selectedSubmission.id,
                answers: answers
            };

            console.log('Submitting report with data:', submissionData);

            const token = localStorage.getItem('jwt');
            const response = await axios.post('/api/v1/reports/submissions/answers', submissionData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Get the updated submission data from the response
            const updatedSubmission = response.data;

            // Update the UI to reflect the completed report
            setDogReports(prevState => {
                // Create a deep copy of the state to avoid mutation
                const newState = { ...prevState };

                // Update the submissions array with the new data
                newState.submissions = newState.submissions.map(submission =>
                    submission.id === updatedSubmission.id ? updatedSubmission : submission
                );

                return newState;
            });

            // Close the modal and show success message
            setFillModalOpen(false);
            showNotification('Raport został pomyślnie zapisany');

            // Update the selectedSubmission with the response data if it's selected
            if (selectedSubmission && selectedSubmission.id === updatedSubmission.id) {
                setSelectedSubmission(updatedSubmission);
            }

            // Reset the active submission state to collapse any open panels
            setActiveSubmissionId(null);

            // No need to reload the page, as we've updated the state

        } catch (err) {
            console.error('Error submitting report:', err.response?.data || err);
            // Show a more detailed error message if available
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                'Nie udało się zapisać raportu';
            showNotification(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Show notification
    const showNotification = (message, type = 'success') => {
        // Remove any existing notifications first
        const existingNotifications = document.querySelectorAll('.owner-reports-success-notification, .owner-reports-error-notification');
        existingNotifications.forEach(notification => {
            document.body.removeChild(notification);
        });

        // Create new notification
        const notification = document.createElement('div');
        notification.className = type === 'success' ? 'owner-reports-success-notification' : 'owner-reports-error-notification';

        // Set inline styles to ensure correct positioning
        notification.style.position = 'fixed';
        notification.style.bottom = '20px';
        notification.style.right = '20px';
        notification.style.top = 'auto'; // Prevent top positioning
        notification.style.left = 'auto'; // Prevent left positioning
        notification.style.height = 'auto';

        // Create text content
        notification.textContent = message;

        // Append to body
        document.body.appendChild(notification);

        // Remove after timeout
        setTimeout(() => {
            if (document.body.contains(notification)) {
                document.body.removeChild(notification);
            }
        }, 3000);
    };

    if (loading && dogReports.assignments.length === 0) return <div>Ładowanie raportów...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="owner-reports-tab">
            {submissionsByAssignment.length === 0 ? (
                <div className="owner-reports-empty-state">
                    <p className="owner-reports-empty-title">Brak przypisanych raportów</p>
                    <img src="/petboard-empty.png" alt="No reports" className="owner-reports-empty-image" />
                </div>
            ) : (
                <div className="owner-reports-assignments-list">
                    {submissionsByAssignment.map(({ assignment, submissions }) => {
                        // Check if assignment has ended
                        const assignmentEndDate = assignment.endDate ? new Date(assignment.endDate) : null;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        let assignmentHasEnded = false;

                        if (assignmentEndDate) {
                            assignmentEndDate.setHours(23, 59, 59, 999);
                            assignmentHasEnded = today > assignmentEndDate;
                        }

                        return (
                            <div key={assignment.id} className="owner-reports-assignment-section">
                                <div className="owner-reports-assignment-header">
                                    <div className="owner-reports-assignment-info">
                                        <h3 className="owner-reports-assignment-template">{assignment.reportTemplateTitle}</h3>
                                        <div className="owner-reports-assignment-meta">
                                            <span className="owner-reports-recurrence-badge">
                                                <FiRepeat className="owner-reports-meta-icon" />
                                                {getRecurrenceLabel(assignment.recurrence)}
                                            </span>
                                            <span className="owner-reports-date-badge">
                                                <FiCalendar className="owner-reports-meta-icon" />
                                                Rozpoczęto: {formatDate(assignment.startDate)}
                                            </span>
                                            {assignment.endDate && (
                                                <span className={`owner-reports-date-badge ${assignmentHasEnded ? 'ended' : 'active'}`}>
                                                    <FiCalendar className="owner-reports-meta-icon" />
                                                    {assignmentHasEnded ? 'Zakończono:' : 'Zakończy się:'} {formatDate(assignment.endDate)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="owner-reports-submissions-timeline">
                                    {submissions.length === 0 ? (
                                        <div className="owner-reports-no-submissions">
                                            Brak dostępnych wypełnień dla tego raportu
                                        </div>
                                    ) : (
                                        <>
                                            <h4 className="owner-reports-timeline-title">Raporty:</h4>
                                            <div className="owner-reports-submissions-list">
                                                {submissions.map(submission => {
                                                    // Check if the due date is today or in the past
                                                    const dueDate = new Date(submission.dueDate);
                                                    dueDate.setHours(0, 0, 0, 0); // Set to beginning of the day

                                                    const today = new Date();
                                                    today.setHours(0, 0, 0, 0); // Set to beginning of today

                                                    // Determine if submission is overdue (due date is today or in the past)
                                                    const isOverdue = dueDate <= today;

                                                    // A submission is pending if it's overdue, not completed, AND the assignment hasn't ended
                                                    const isPending = isOverdue && !submission.completed && !assignmentHasEnded;

                                                    // A submission is upcoming if it's in the future
                                                    const isUpcoming = dueDate > today;

                                                    // A submission is expired if the assignment has ended and it's not completed
                                                    const isExpired = assignmentHasEnded && !submission.completed;

                                                    return (
                                                        <div
                                                            key={submission.id}
                                                            className={`owner-reports-submission-item ${
                                                                submission.completed ? 'completed' :
                                                                    isExpired ? 'expired' :
                                                                        isPending ? 'pending' :
                                                                            'upcoming'
                                                            } ${activeSubmissionId === submission.id ? 'active' : ''}`}
                                                            onClick={(e) => handleSubmissionClick(e, submission.id)}
                                                        >
                                                            <div className="owner-reports-submission-header">
                                                                <div className="owner-reports-status-badge-small">
                                                                    {submission.completed ? (
                                                                        <>
                                                                            <FiCheckCircle className="owner-reports-status-icon" />
                                                                            <span>Wypełniony</span>
                                                                        </>
                                                                    ) : isExpired ? (
                                                                        <>
                                                                            <FiClock className="owner-reports-status-icon" />
                                                                            <span>Wygasł</span>
                                                                        </>
                                                                    ) : isPending ? (
                                                                        <>
                                                                            <FiClock className="owner-reports-status-icon" />
                                                                            <span>Do wypełnienia</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FiCalendar className="owner-reports-status-icon" />
                                                                            <span>Nadchodzący</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <div className="owner-reports-submission-date">
                                                                    <FiCalendar className="owner-reports-date-icon" />
                                                                    {formatDate(submission.dueDate)}
                                                                    {submission.completed && submission.submittedAt && (
                                                                        <span className="owner-reports-submitted-at">
                                                                            (Wypełniono: {formatDate(submission.submittedAt)})
                                                                        </span>
                                                                    )}
                                                                    {assignmentHasEnded && !submission.completed && (
                                                                        <span className="owner-reports-expired-note">
                                                                            (Zadanie zakończone: {formatDate(assignment.endDate)})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Submission details when clicked */}
                                                            {activeSubmissionId === submission.id && (
                                                                <div className="owner-reports-submission-details">
                                                                    <h4>Szczegóły raportu:</h4>

                                                                    {submission.completed ? (
                                                                        <>
                                                                            <div className="owner-reports-answers-list">
                                                                                {submission.answers.map((answer, index) => (
                                                                                    <div key={answer.id} className="owner-reports-answer-item">
                                                                                        <div className="owner-reports-answer-question">
                                                                                            <span className="owner-reports-question-number">{index + 1}.</span>
                                                                                            <span className="owner-reports-question-text">{answer.questionText}</span>
                                                                                        </div>
                                                                                        <div className="owner-reports-answer-text">
                                                                                            {answer.answerText || <em>Brak odpowiedzi</em>}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                            <div className="owner-reports-view-report-actions">
                                                                                <button
                                                                                    className="owner-reports-view-full-report-btn"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleViewSubmission(submission);
                                                                                    }}
                                                                                >
                                                                                    Zobacz pełny raport
                                                                                </button>
                                                                            </div>
                                                                        </>
                                                                    ) : isExpired ? (
                                                                        <div className="owner-reports-expired-report">
                                                                            <p>Ten raport nie może być już wypełniony - zadanie zakończyło się {formatDate(assignment.endDate)}.</p>
                                                                        </div>
                                                                    ) : isPending ? (
                                                                        <div className="owner-reports-report-actions">
                                                                            <p>Ten raport oczekuje na wypełnienie.</p>
                                                                            <button
                                                                                className="owner-reports-fill-report-btn"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleFillReport(submission);
                                                                                }}
                                                                            >
                                                                                Wypełnij teraz
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="owner-reports-no-answers">
                                                                            Ten raport będzie dostępny do wypełnienia w przyszłości.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Fill Report Modal */}
            {fillModalOpen && selectedSubmission && (
                <div className="owner-reports-modal-overlay">
                    <div className="owner-reports-modal owner-reports-large-modal">
                        <div className="owner-reports-modal-header">
                            <h2 className="owner-reports-modal-title">Wypełnij raport: {selectedSubmission.template.title}</h2>
                            <button
                                className="owner-reports-close-btn"
                                onClick={() => setFillModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="owner-reports-modal-content">
                            <div className="owner-reports-report-meta">
                                <span>Data wypełnienia: {formatDate(selectedSubmission.dueDate)}</span>
                            </div>

                            {selectedSubmission.template.questions && selectedSubmission.template.questions.length > 0 ? (
                                <div className="owner-reports-questions-list">
                                    {selectedSubmission.template.questions
                                        .sort((a, b) => a.orderIndex - b.orderIndex)
                                        .map((question, index) => (
                                            <div key={question.id} className="owner-reports-question-item">
                                                <div className="owner-reports-question-header">
                                                    <div className="owner-reports-question-number">{index + 1}.</div>
                                                    <div className="owner-reports-question-text">{question.questionText}</div>
                                                </div>
                                                <div className="owner-reports-answer-input">
                                                    <textarea
                                                        placeholder="Wpisz swoją odpowiedź..."
                                                        value={formAnswers[question.id] || ''}
                                                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                        rows="3"
                                                        required
                                                    ></textarea>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                <div className="owner-reports-empty-questions-message">
                                    Ten raport nie zawiera żadnych pytań.
                                </div>
                            )}
                        </div>

                        <div className="owner-reports-modal-footer">
                            <button
                                className="owner-reports-cancel-btn"
                                onClick={() => setFillModalOpen(false)}
                                disabled={loading}
                            >
                                Anuluj
                            </button>
                            {selectedSubmission.template.questions && selectedSubmission.template.questions.length > 0 && (
                                <button
                                    className="owner-reports-save-btn"
                                    onClick={handleSubmitReport}
                                    disabled={loading}
                                >
                                    {loading ? 'Zapisywanie...' : 'Zapisz raport'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* View Full Report Modal */}
            {viewSubmissionOpen && selectedSubmission && (
                <div className="owner-reports-modal-overlay">
                    <div className="owner-reports-modal owner-reports-large-modal">
                        <div className="owner-reports-modal-header">
                            <h2 className="owner-reports-modal-title">Raport: {selectedSubmission.template.title}</h2>
                            <button
                                className="owner-reports-close-btn"
                                onClick={() => setViewSubmissionOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="owner-reports-modal-content">
                            <div className="owner-reports-submission-meta">
                                <div className="owner-reports-meta-row">
                                    <span className="owner-reports-meta-label">Data raportu:</span>
                                    <span className="owner-reports-meta-value">{formatDate(selectedSubmission.dueDate)}</span>
                                </div>
                                {selectedSubmission.submittedAt && (
                                    <div className="owner-reports-meta-row">
                                        <span className="owner-reports-meta-label">Data wypełnienia:</span>
                                        <span className="owner-reports-meta-value">{formatDate(selectedSubmission.submittedAt)}</span>
                                    </div>
                                )}
                                <div className="owner-reports-meta-row">
                                    <span className="owner-reports-meta-label">Status:</span>
                                    <span className="owner-reports-meta-value completed">
                                        <FiCheckCircle className="owner-reports-status-icon-small" /> Wypełniony
                                    </span>
                                </div>
                            </div>

                            <div className="owner-reports-answers-section">
                                <h3>Twoje odpowiedzi:</h3>
                                <div className="owner-reports-answers-list">
                                    {selectedSubmission.answers.map((answer, index) => (
                                        <div key={answer.id} className="owner-reports-answer-item">
                                            <div className="owner-reports-answer-question">
                                                <span className="owner-reports-question-number">{index + 1}.</span>
                                                <span className="owner-reports-question-text">{answer.questionText}</span>
                                            </div>
                                            <div className="owner-reports-answer-text">
                                                {answer.answerText || <em>Brak odpowiedzi</em>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="owner-reports-modal-footer">
                            <button
                                className="owner-reports-cancel-btn"
                                onClick={() => setViewSubmissionOpen(false)}
                            >
                                Zamknij
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OwnerReportsTab;